import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, Input, Button, Empty, Spin, Tooltip, Typography, Modal, message } from "antd";
import {
  SearchOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  VideoCameraOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getUpcomingAppointments,
  getDoctorAppointmentList,
  patientbyDoctor,
  cancelAppointment,
} from "../../redux/doctorSlice";
import { mergeAppointments, splitUpcomingPast, SOURCE_ZOOM } from "../../../utils/normalizeAppointments";
// The counterparty on this screen is the patient, not a clinician - the
// clinician default avatar (lab coat + stethoscope) would misrepresent them.
import neutralPatientAvatar from "../../../assets/images/users/patient_default_avatar.svg";
import "../PatientDashboard/PatientAppointments.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

function normalizeName(value) {
  return String(value || "").trim().toLowerCase();
}

/**
 * Cancel confirmation gate - collects an optional reason before calling
 * Doctor/CancelAppointment.
 */
function CancelAppointmentModal({ open, appt, submitting, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <Modal
      title="Cancel Appointment"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={520}
      destroyOnClose
    >
      <Text style={{ display: "block", color: "#595959", marginBottom: 12 }}>
        {appt
          ? `Cancel the appointment with ${appt.providerName || "this patient"} on ${appt.startMoment.format(
              "MMMM D, YYYY"
            )} at ${appt.startMoment.format("h:mmA")}?`
          : "Cancel this appointment?"}
      </Text>
      <TextArea
        rows={3}
        placeholder="Reason for cancelling (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={500}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
        <Button onClick={onCancel} disabled={submitting}>
          Keep appointment
        </Button>
        <Button danger type="primary" loading={submitting} onClick={() => onConfirm(reason)}>
          Cancel appointment
        </Button>
      </div>
    </Modal>
  );
}

export default function DoctorAppointments() {
  const dispatch = useDispatch();

  const { upcomingAppointments, zoomAppointments } = useSelector((state) => state.doctor);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [photoMap, setPhotoMap] = useState(() => new Map());
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Two unreconciled backend stores: Doctor/GetAllAppointment (legacy, returns
  // past + future) and Doctor/GetDrappointmentList (Zoom bookings). Both are
  // fetched and merged; either can fail without blanking the screen.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      dispatch(getUpcomingAppointments()),
      dispatch(getDoctorAppointmentList()),
    ]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // The appointment DTO carries no patient photo, so resolve it from the doctor's
  // own patient list (GetPatientByDoctor exposes a picture). Best-effort.
  useEffect(() => {
    let cancelled = false;

    const fetchPatientPhotos = async () => {
      try {
        const res = await dispatch(patientbyDoctor({ page: 0, size: 1000 }));
        const list = res?.payload?.data;
        const rows = Array.isArray(list) ? list : [];

        const map = new Map();
        rows.forEach((p) => {
          const photo = p?.picture || p?.Picture || p?.profile || p?.Profile;
          if (!photo) return;
          const fullName = `${p?.firstname ?? p?.Firstname ?? ""} ${p?.lastname ?? p?.Lastname ?? ""}`;
          const key = normalizeName(fullName);
          if (key) map.set(key, photo);
          const userRef = p?.userRef ?? p?.UserRef;
          if (userRef) map.set(String(userRef), photo);
        });

        if (!cancelled) setPhotoMap(map);
      } catch (e) {
        // Photos are best-effort; fall back to the neutral avatar for everyone.
        if (!cancelled) setPhotoMap(new Map());
      }
    };

    fetchPatientPhotos();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const photoFor = useCallback(
    (appt) => photoMap.get(normalizeName(appt?.providerName)) || neutralPatientAvatar,
    [photoMap]
  );

  const allAppointments = useMemo(
    () =>
      mergeAppointments({
        legacy: upcomingAppointments,
        zoom: zoomAppointments,
        viewer: "doctor",
      }),
    [upcomingAppointments, zoomAppointments]
  );

  const filteredBySearch = useMemo(() => {
    if (!searchParam) return allAppointments;
    const q = searchParam.toLowerCase();
    return allAppointments.filter((a) => normalizeName(a?.providerName).includes(q));
  }, [allAppointments, searchParam]);

  const { upcoming, past } = useMemo(
    () => splitUpcomingPast(filteredBySearch),
    [filteredBySearch]
  );

  const handleConfirmCancel = useCallback(
    (reason) => {
      if (!cancelTarget?.id) return;
      setCancelling(true);
      dispatch(cancelAppointment({ appointmentId: cancelTarget.id, reason }))
        .then((response) => {
          if (cancelAppointment.fulfilled.match(response)) {
            message.success("Appointment cancelled.");
            setCancelTarget(null);
          } else {
            message.error(
              response?.payload?.message || response?.payload?.Message || "Failed to cancel appointment."
            );
          }
        })
        .finally(() => setCancelling(false));
    },
    [cancelTarget, dispatch]
  );

  const renderCard = (appt, isPast) => {
    const dateLabel = appt.startMoment.format("MMMM D, YYYY");
    const timeLabel = appt.endMoment
      ? `${appt.startMoment.format("h:mmA")} - ${appt.endMoment.format("h:mmA")}`
      : appt.startMoment.format("h:mmA");

    return (
      <div
        className="pa-card"
        key={`${appt.source}-${appt.id ?? ""}-${appt.startMoment.valueOf()}`}
      >
        <img
          className="pa-card-avatar"
          src={photoFor(appt)}
          alt=""
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = neutralPatientAvatar;
          }}
        />

        <div className="pa-card-provider">
          <h3 className="pa-card-name">{appt.providerName || "Patient"}</h3>
        </div>

        <div className="pa-card-meta">
          <div className="pa-card-meta-row">
            <CalendarOutlined className="pa-card-meta-icon" aria-hidden />
            <span>{dateLabel}</span>
          </div>
          {!!timeLabel && (
            <div className="pa-card-meta-row">
              <ClockCircleOutlined className="pa-card-meta-icon" aria-hidden />
              <span>{timeLabel}</span>
            </div>
          )}
          <div className="pa-card-meta-row">
            <EnvironmentOutlined className="pa-card-meta-icon" aria-hidden />
            <span>{appt.locationLabel}</span>
          </div>
        </div>

        <div className="pa-card-action" style={{ display: "flex", gap: 8 }}>
          {!isPast && (
            <Tooltip
              title={
                appt.meetingLink
                  ? ""
                  : "Join link not available yet."
              }
            >
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                disabled={!appt.meetingLink}
                style={
                  appt.meetingLink
                    ? { background: "#00ADEF", borderColor: "#00ADEF" }
                    : undefined
                }
                onClick={() => window.open(appt.meetingLink, "_blank")}
              >
                JOIN
              </Button>
            </Tooltip>
          )}
          {/* Cancellation only works for Zoom-sourced bookings - CancelAppointment
              looks the id up in the Appointment table, which legacy records aren't in. */}
          {!isPast && appt.source === SOURCE_ZOOM && !!appt.id && (
            <Button danger icon={<CloseCircleOutlined />} onClick={() => setCancelTarget(appt)}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderList = (items, isPast) => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      );
    }
    if (!items.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={isPast ? "No past appointments" : "No upcoming appointments"}
          style={{ padding: 48 }}
        />
      );
    }
    return <div className="pa-card-list">{items.map((a) => renderCard(a, isPast))}</div>;
  };

  const tabItems = [
    {
      key: "upcoming",
      label: `Upcoming (${upcoming.length})`,
      children: renderList(upcoming, false),
    },
    {
      key: "past",
      label: `Past (${past.length})`,
      children: renderList(past, true),
    },
  ];

  return (
    <div className="pa-wrapper">
      <Title level={3} className="pa-heading">
        Appointments
      </Title>

      <Tabs
        items={tabItems}
        tabBarExtraContent={
          <Input
            placeholder="Search"
            value={searchParam}
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchParam(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
        }
      />

      <CancelAppointmentModal
        open={!!cancelTarget}
        appt={cancelTarget}
        submitting={cancelling}
        onCancel={() => setCancelTarget(null)}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
}
